import React from "react";
import styles from "./RegisterOrderForm.module.css";

export type OrderTypeOption<T extends string> = {
	value: T;
	label?: string;
	description?: string;
	icon: React.ReactNode;
};

type OrderTypePickerProps<T extends string> = {
	/** Shared by the radios, which is what makes the arrow keys move between them. */
	name: string;
	value: T;
	options: OrderTypeOption<T>[];
	onChange: (value: T) => void;
	ariaLabel?: string;
};

/**
 * A single choice shown as cards: every option visible at once, with a line on what it means.
 *
 * Built on native radios, so picking one clears the others by construction — the mutual exclusion
 * the page used to enforce with an error toast after the fact.
 */
function OrderTypePicker<T extends string>({
	name,
	value,
	options,
	onChange,
	ariaLabel,
}: OrderTypePickerProps<T>) {
	return (
		<div
			role="radiogroup"
			aria-label={ariaLabel}
			className={styles.orderTypes}
		>
			{options.map((option) => {
				const selected = option.value === value;
				return (
					<label
						key={option.value}
						className={`${styles.orderType} ${
							selected ? styles.orderTypeSelected : ""
						}`}
					>
						<input
							type="radio"
							className={styles.visuallyHidden}
							name={name}
							value={option.value}
							checked={selected}
							onChange={() => onChange(option.value)}
						/>
						<span
							className={styles.orderTypeIcon}
							aria-hidden="true"
						>
							{option.icon}
						</span>
						<span className={styles.orderTypeText}>
							<span className={styles.orderTypeLabel}>
								{option.label}
							</span>
							{option.description && (
								<span className={styles.orderTypeDescription}>
									{option.description}
								</span>
							)}
						</span>
						<span
							className={styles.orderTypeCheck}
							aria-hidden="true"
						/>
					</label>
				);
			})}
		</div>
	);
}

export default OrderTypePicker;
